/**
 * Medir un modelo IFC SIN dibujarlo: el mismo recorrido que hace el visor (`ifc.worker.ts`) para sacar el
 * inventario —inventariar, acumular la malla de cada pieza, reconstruir lo que web-ifc no dibuja, reconocer los
 * materiales sin tipo, cerrar y leer las propiedades del tipo—, pero sin armar lotes, colores ni aristas.
 *
 * Lo usan la comparación de revisiones («Cambió el plano», en un Worker propio: `ifcMedir.worker.ts`) y la vara
 * en node (`vara-ifc/varaCambio.ts`). La medición en sí vive en `medicionIfc.ts` (`crearMedidor`): aquí no se
 * decide nada, solo se recorre igual que el visor para que el inventario salga IDÉNTICO al que vio quien armó
 * la hoja (si no, la autocomprobación de la comparación falla y todo sale «revisar»).
 */
import type { ParametrosMedicionIfc, PlanoInventario, TipoMaterialIfc } from '@supernova/shared';
import { crearMedidor, type NivelIfc } from './medicionIfc.js';
import { leerPropiedadesPorTipo } from './propiedadesIfc.js';

/** Lo que queda de medir un modelo: lo mismo que el visor entrega a la pantalla (`onInventario` + `onNiveles`). */
export interface MedicionDeModelo {
  capas: PlanoInventario['capas'];
  /** Los niveles con piezas (los vacíos no cuentan, igual que en la pantalla). */
  niveles: NivelIfc[];
  nivelDeElemento: Map<number, number>;
  ifc: NonNullable<PlanoInventario['ifc']>;
}

export interface OpcionesMedirModelo {
  /** Avance del inventario (0–1) y de la lectura de la geometría (0–1). */
  onAvance?: (fase: 'inventario' | 'geometria' | 'cierre', fraccion: number | null) => void;
  /**
   * Reconoce por su nombre los materiales que el modelo no tipifica (el visor se lo pide a la API, con caché).
   * Sin esta función (la vara) esas piezas quedan sin pesar, igual que en el visor sin archivo del proyecto.
   */
  tiposDeMaterial?: (nombres: string[]) => Promise<{ materiales: { nombre: string; tipo: TipoMaterialIfc }[]; aviso: string | null } | null>;
}

/** Mide un modelo ya abierto (`api.OpenModel`). No lo cierra: lo cierra quien lo abrió. */
export async function medirModeloIfc(
  api: any,
  WebIFC: any,
  modelID: number,
  parametros: ParametrosMedicionIfc,
  o: OpcionesMedirModelo = {},
): Promise<MedicionDeModelo> {
  const medidor = crearMedidor(api, WebIFC, modelID, parametros, (f) => o.onAvance?.('inventario', f));
  o.onAvance?.('inventario', 0);
  const expressAEtiqueta = await medidor.inventariar();

  // Igual que el visor: se pregunta por los materiales sin tipo mientras se lee la geometría.
  const sinTipo = medidor.materialesSinTipo();
  const tipos = sinTipo.length && o.tiposDeMaterial ? o.tiposDeMaterial(sinTipo.map((m) => m.nombre)) : Promise.resolve(null);

  const total = Math.max(1, expressAEtiqueta.size);
  const vistas = new Set<number>();
  let ultimo = 0;
  o.onAvance?.('geometria', 0);
  api.StreamAllMeshes(modelID, (flat: any) => {
    const eid: number = flat.expressID;
    const placed = flat.geometries;
    for (let i = 0; i < placed.size(); i++) {
      const pg = placed.get(i);
      const g = api.GetGeometry(modelID, pg.geometryExpressID);
      const verts = api.GetVertexArray(g.GetVertexData(), g.GetVertexDataSize());
      const idx = api.GetIndexArray(g.GetIndexData(), g.GetIndexDataSize());
      const n = verts.length / 6;
      if (n === 0 || idx.length === 0) {
        g.delete?.();
        continue; // pieza sin malla: nada que medir
      }
      const pos = new Float32Array(n * 3);
      for (let v = 0, k = 0; v < verts.length; v += 6, k += 3) {
        pos[k] = verts[v];
        pos[k + 1] = verts[v + 1];
        pos[k + 2] = verts[v + 2];
      }
      medidor.acumular(eid, pos, idx, pg.flatTransformation);
      g.delete?.();
    }
    if (expressAEtiqueta.has(eid)) vistas.add(eid);
    const ahora = Date.now();
    if (ahora - ultimo > 150) {
      ultimo = ahora;
      o.onAvance?.('geometria', Math.min(0.99, vistas.size / total));
    }
  });
  // Lo que web-ifc no dibuja (perfil de eje): el medidor lo arma y lo mide al recorrerlo.
  for (const _ of medidor.reconstruirSinMalla()) void _;

  o.onAvance?.('cierre', null);
  const t = await tipos;
  if (t) medidor.aplicarTiposDeMaterial(t.materiales);
  const r = medidor.cerrar();
  if (t?.aviso) r.ifc.avisoMateriales = t.aviso;
  try {
    const props = await leerPropiedadesPorTipo(api, WebIFC, modelID, expressAEtiqueta);
    for (const c of r.capas) {
      const pr = props.get(c.capa);
      if (pr) c.ifc = { ...c.ifc, ...pr };
    }
  } catch {
    /* sin propiedades: los nombres quedan con la clase y lo medido (igual que el visor) */
  }
  return {
    capas: r.capas,
    niveles: r.niveles.filter((n) => n.elementos > 0),
    nivelDeElemento: r.nivelDeElemento,
    ifc: r.ifc,
  };
}

/** El inventario que la pantalla arma con lo que entrega el visor (mismo armado que `IfcViewer3D`). */
export function inventarioDeMedicion(m: Pick<MedicionDeModelo, 'capas' | 'ifc'>): PlanoInventario {
  return {
    bloques: [],
    capas: m.capas,
    total_inserts: 0,
    tipos_de_bloque: 0,
    unidades: { codigo: 0, nombre: 'm', factor_a_metro: 1 },
    ifc: m.ifc,
  };
}
